import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import {
  AuthResponse,
  LoginCredentials,
  User,
  Layout,
  Plot,
  PlotStatus,
  CreateLayoutRequest,
  UpdatePlotRequest,
  BulkPlotRequest,
  LayoutReport,
  RevenueReport,
  DashboardStats,
  PlotFilters,
  LayoutFilters,
  ApiResponse,
  PaginatedResponse,
  StatusDistributionReport,
  PlotsResponse,
} from '../types';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle token refresh
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const refreshToken = localStorage.getItem('refreshToken');
            if (refreshToken) {
              const response = await this.refreshToken(refreshToken);
              const { accessToken } = response.data;
              
              localStorage.setItem('accessToken', accessToken);
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
              
              return this.api(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, redirect to login
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            window.location.href = '/login';
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Generic request methods
  private async request<T>(config: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.api(config);
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'An error occurred';
      throw new Error(message);
    }
  }

  private get<T>(url: string, params?: any): Promise<T> {
    return this.request<T>({ method: 'GET', url, params });
  }

  private post<T>(url: string, data?: any): Promise<T> {
    return this.request<T>({ method: 'POST', url, data });
  }

  private put<T>(url: string, data?: any): Promise<T> {
    return this.request<T>({ method: 'PUT', url, data });
  }

  private delete<T>(url: string): Promise<T> {
    return this.request<T>({ method: 'DELETE', url });
  }

  // Authentication endpoints
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    return this.post<AuthResponse>('/auth/login', credentials);
  }

  async logout(): Promise<ApiResponse<any>> {
    return this.post<ApiResponse<any>>('/auth/logout');
  }

  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    return this.post<AuthResponse>('/auth/refresh', { refreshToken });
  }

  async changePassword(data: { currentPassword: string; newPassword: string }): Promise<ApiResponse<any>> {
    return this.post<ApiResponse<any>>('/auth/change-password', data);
  }

  async getProfile(): Promise<ApiResponse<User>> {
    return this.get<ApiResponse<User>>('/auth/me');
  }

  // User management endpoints
  async getUsers(params?: { page?: number; limit?: number; search?: string; role?: string }): Promise<PaginatedResponse<User>> {
    return this.get<PaginatedResponse<User>>('/users', params);
  }

  async createUser(userData: Partial<User> & { password: string }): Promise<ApiResponse<User>> {
    return this.post<ApiResponse<User>>('/users', userData);
  }

  async updateUser(id: number, userData: Partial<User>): Promise<ApiResponse<User>> {
    return this.put<ApiResponse<User>>(`/users/${id}`, userData);
  }

  async deleteUser(id: number): Promise<ApiResponse<any>> {
    return this.delete<ApiResponse<any>>(`/users/${id}`);
  }

  // Layout endpoints
  async getLayouts(filters?: LayoutFilters): Promise<PaginatedResponse<Layout>> {
    return this.get<PaginatedResponse<Layout>>('/layouts', filters);
  }

  async getLayout(id: number): Promise<ApiResponse<Layout>> {
    return this.get<ApiResponse<Layout>>(`/layouts/${id}`);
  }

  async createLayout(data: CreateLayoutRequest): Promise<ApiResponse<Layout>> {
    return this.post<ApiResponse<Layout>>('/layouts', data);
  }

  async updateLayout(id: number, data: Partial<CreateLayoutRequest>): Promise<ApiResponse<Layout>> {
    return this.put<ApiResponse<Layout>>(`/layouts/${id}`, data);
  }

  async deleteLayout(id: number): Promise<ApiResponse<any>> {
    return this.delete<ApiResponse<any>>(`/layouts/${id}`);
  }

  async assignUserToLayout(layoutId: number, data: { user_id: number; can_update?: boolean; can_view_reports?: boolean }): Promise<ApiResponse<any>> {
    return this.post<ApiResponse<any>>(`/layouts/${layoutId}/assign-user`, data);
  }

  async removeUserFromLayout(layoutId: number, userId: number): Promise<ApiResponse<any>> {
    return this.delete<ApiResponse<any>>(`/layouts/${layoutId}/users/${userId}`);
  }

  async createBulkPlots(layoutId: number, data: BulkPlotRequest): Promise<ApiResponse<any>> {
    return this.post<ApiResponse<any>>(`/layouts/${layoutId}/bulk-plots`, data);
  }

  async getLayoutPlots(layoutId: number, params?: PlotFilters): Promise<PlotsResponse> {
    return this.get<PlotsResponse>(`/layouts/${layoutId}/plots`, params);
  }

  // Plot endpoints
  async getPlots(filters?: PlotFilters): Promise<PlotsResponse> {
    return this.get<PlotsResponse>('/plots', filters);
  }

  async getPlot(id: number): Promise<ApiResponse<{ plot: Plot; statusHistory: any[] }>> {
    return this.get<ApiResponse<{ plot: Plot; statusHistory: any[] }>>(`/plots/${id}`);
  }

  async updatePlot(id: number, data: UpdatePlotRequest): Promise<ApiResponse<{ plot: Plot }>> {
    return this.put<ApiResponse<{ plot: Plot }>>(`/plots/${id}`, data);
  }

  async deletePlot(id: number): Promise<ApiResponse<any>> {
    return this.delete<ApiResponse<any>>(`/plots/${id}`);
  }

  async getPlotHistory(id: number): Promise<ApiResponse<{ history: any[] }>> {
    return this.get<ApiResponse<{ history: any[] }>>(`/plots/${id}/history`);
  }

  async searchPlots(searchData: any): Promise<PaginatedResponse<Plot>> {
    return this.post<PaginatedResponse<Plot>>('/plots/search', searchData);
  }

  async bulkUpdatePlots(data: { plot_ids: number[]; updates: Partial<UpdatePlotRequest>; notes?: string }): Promise<ApiResponse<any>> {
    return this.post<ApiResponse<any>>('/plots/bulk-update', data);
  }

  async getPlotStatuses(): Promise<ApiResponse<{ statuses: PlotStatus[] }>> {
    return this.get<ApiResponse<{ statuses: PlotStatus[] }>>('/plots/statuses');
  }

  // Report endpoints
  async getLayoutReport(id: number): Promise<ApiResponse<LayoutReport>> {
    return this.get<ApiResponse<LayoutReport>>(`/reports/layout/${id}`);
  }

  async getRevenueReport(params?: { 
    layout_ids?: string; 
    start_date?: string; 
    end_date?: string; 
    group_by?: string 
  }): Promise<ApiResponse<RevenueReport>> {
    return this.get<ApiResponse<RevenueReport>>('/reports/revenue', params);
  }

  async getStatusDistribution(params?: { layout_ids?: string }): Promise<ApiResponse<StatusDistributionReport>> {
    return this.get<ApiResponse<StatusDistributionReport>>('/reports/status-distribution', params);
  }

  async getUserActivityReport(params?: { 
    start_date?: string; 
    end_date?: string; 
    user_ids?: string 
  }): Promise<ApiResponse<any>> {
    return this.get<ApiResponse<any>>('/reports/user-activity', params);
  }

  async exportToCSV(params: { 
    report_type: string; 
    layout_id?: string; 
    start_date?: string; 
    end_date?: string 
  }): Promise<Blob> {
    const response = await this.api({
      method: 'GET',
      url: '/reports/export/csv',
      params,
      responseType: 'blob',
    });
    return response.data;
  }

  async exportToExcel(params: { 
    report_type: string; 
    layout_id?: string; 
    start_date?: string; 
    end_date?: string 
  }): Promise<Blob> {
    const response = await this.api({
      method: 'GET',
      url: '/reports/export/excel',
      params,
      responseType: 'blob',
    });
    return response.data;
  }

  // Dashboard endpoints
  async getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
    return this.get<ApiResponse<DashboardStats>>('/dashboard/overview');
  }

  async getQuickStats(): Promise<ApiResponse<any>> {
    return this.get<ApiResponse<any>>('/dashboard/quick-stats');
  }

  async getActivityFeed(): Promise<ApiResponse<any>> {
    return this.get<ApiResponse<any>>('/dashboard/activity-feed');
  }

  async getSmartNotifications(): Promise<ApiResponse<any>> {
    return this.get<ApiResponse<any>>('/dashboard/notifications');
  }

  // Health check
  async healthCheck(): Promise<any> {
    return this.get<any>('/health');
  }

  // New: get layout details with plot stats and assigned users
  async getLayoutDetails(id: number): Promise<ApiResponse<{ layout: Layout; plotStats: any[]; assignedUsers: any[] }>> {
    return this.get<ApiResponse<{ layout: Layout; plotStats: any[]; assignedUsers: any[] }>>(`/layouts/${id}`);
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService; 